if (!customElements.get('delivery-estimate')) {
  const DAY = 86400000;
  const pad = (n) => String(n).padStart(2, '0');
  const dateKey = (d) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
  const addDays = (d, n) => new Date(d.getTime() + n * DAY);

  // Dates are calendar days held as UTC midnights, so formatting must use UTC too
  const longDate = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' });
  const shortDate = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' });

  /**
   * Nth weekday of a month (n = -1 for the last one).
   */
  const nthWeekday = (year, month, weekday, n) => {
    if (n > 0) {
      const first = new Date(Date.UTC(year, month, 1));
      return addDays(first, ((weekday - first.getUTCDay() + 7) % 7) + (n - 1) * 7);
    }
    const last = new Date(Date.UTC(year, month + 1, 0));
    return addDays(last, -((last.getUTCDay() - weekday + 7) % 7));
  };

  /**
   * A fixed-date holiday moves to the Friday before or Monday after when it falls on a weekend.
   */
  const observed = (year, month, day) => {
    const d = new Date(Date.UTC(year, month, day));
    const weekday = d.getUTCDay();
    if (weekday === 6) return addDays(d, -1);
    if (weekday === 0) return addDays(d, 1);
    return d;
  };

  /**
   * Days the major US carriers don't pick up or deliver: New Year's Day, Memorial Day, Independence Day, Labor Day,
   * Thanksgiving and Christmas.
   */
  const carrierHolidays = (year) => [
    observed(year, 0, 1),
    nthWeekday(year, 4, 1, -1),
    observed(year, 6, 4),
    nthWeekday(year, 8, 1, 1),
    nthWeekday(year, 10, 4, 4),
    observed(year, 11, 25)
  ].map(dateKey);

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.js-product');
      this.cutoff = parseInt(this.dataset.cutoff, 10) * 60;
      [this.processingMin, this.processingMax] = this.dataset.processing.split(',').map(Number);
      [this.transitMin, this.transitMax] = this.dataset.transit.split(',').map(Number);
      this.fastDays = parseInt(this.dataset.fast, 10);
      this.closed = new Set(this.dataset.closed.split(',').map((d) => d.trim()).filter(Boolean));
      this.backordered = this.dataset.backordered.split(',').filter(Boolean).map(Number);

      this.datesEl = this.querySelector('.js-delivery-dates');
      this.subEl = this.querySelector('.js-delivery-sub');
      this.shipText = this.shipText || this.subEl.textContent;
      this.fastEl = this.querySelector('.js-delivery-fast');

      this.variantChangeHandler = this.variantChangeHandler || this.handleVariantChange.bind(this);
      if (this.section) this.section.addEventListener('on:variant:change', this.variantChangeHandler);

      this.update();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      if (this.section) this.section.removeEventListener('on:variant:change', this.variantChangeHandler);
    }

    /**
     * Hides the estimate for variants that are sold out, backordered or don't ship.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      const { variant } = evt.detail;
      this.hidden = !variant || !variant.available || variant.requires_shipping === false
        || this.backordered.includes(variant.id);
    }

    /**
     * Gets today's date and the minutes since midnight in the warehouse's time zone.
     * @returns {{date: Date, minutes: number}}
     */
    shopNow() {
      const parts = {};
      let formatter;
      try {
        formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: this.dataset.timezone,
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          hourCycle: 'h23'
        });
      } catch (err) {
        formatter = new Intl.DateTimeFormat('en-US', {
          year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hourCycle: 'h23'
        });
      }
      formatter.formatToParts(new Date()).forEach((part) => {
        parts[part.type] = parseInt(part.value, 10);
      });
      return {
        date: new Date(Date.UTC(parts.year, parts.month - 1, parts.day)),
        minutes: (parts.hour % 24) * 60 + parts.minute
      };
    }

    /**
     * Works out the dates for the current day and time, then refreshes them on the next minute.
     */
    update() {
      clearTimeout(this.timer);
      const now = this.shopNow();
      const today = now.date;
      const year = today.getUTCFullYear();
      const holidays = new Set([...carrierHolidays(year), ...carrierHolidays(year + 1)]);

      const carrierOpen = (d) => d.getUTCDay() % 6 !== 0 && !holidays.has(dateKey(d));
      const warehouseOpen = (d) => carrierOpen(d) && !this.closed.has(dateKey(d));
      const nextOpen = (d, isOpen) => {
        let next = addDays(d, 1);
        while (!isOpen(next)) next = addDays(next, 1);
        return next;
      };
      const addBusinessDays = (d, n, isOpen) => {
        let result = d;
        for (let i = 0; i < n; i += 1) result = nextOpen(result, isOpen);
        return result;
      };

      // Orders after the cutoff, or on a day the warehouse is shut, start processing on its next open day
      let orderDay = today;
      if (!warehouseOpen(today) || now.minutes >= this.cutoff) orderDay = nextOpen(today, warehouseOpen);

      const shipFrom = addBusinessDays(orderDay, this.processingMin, warehouseOpen);
      const shipTo = addBusinessDays(orderDay, this.processingMax, warehouseOpen);
      const arriveFrom = addBusinessDays(shipFrom, this.transitMin, carrierOpen);
      const arriveTo = addBusinessDays(shipTo, this.transitMax, carrierOpen);

      this.datesEl.textContent = this.formatRange(arriveFrom, arriveTo, today);
      if (this.fastEl) {
        this.fastEl.textContent = `by ${this.formatDate(addBusinessDays(shipTo, this.fastDays, carrierOpen), today)}`;
      }

      // Count down to the cutoff only when it's less than a day away, as on Amazon
      const minutesLeft = Math.round((orderDay - today) / 60000) + this.cutoff - now.minutes;
      if (this.hasAttribute('data-countdown') && minutesLeft > 0 && minutesLeft <= 1440) {
        const time = document.createElement('strong');
        time.textContent = DeliveryEstimate.formatDuration(minutesLeft);
        this.subEl.replaceChildren('Order within ', time);
      } else {
        this.subEl.textContent = this.shipText;
      }

      this.timer = setTimeout(this.update.bind(this), 60000 - (Date.now() % 60000) + 50);
    }

    /**
     * @param {Date} d - Calendar date.
     * @param {Date} today - The warehouse's current date.
     * @returns {string} e.g. "Mon, Nov 9", or "Tomorrow, Oct 31".
     */
    formatDate(d, today) {
      const daysAway = Math.round((d - today) / DAY);
      if (daysAway === 0) return `Today, ${shortDate.format(d)}`;
      if (daysAway === 1) return `Tomorrow, ${shortDate.format(d)}`;
      return longDate.format(d);
    }

    /**
     * @returns {string} e.g. "Mon, Nov 9 – Fri, Nov 13".
     */
    formatRange(from, to, today) {
      if (from.getTime() === to.getTime()) return this.formatDate(from, today);
      return `${this.formatDate(from, today)} – ${this.formatDate(to, today)}`;
    }

    /**
     * @param {number} minutes - Minutes left.
     * @returns {string} e.g. "3 hrs 12 mins".
     */
    static formatDuration(minutes) {
      const hrs = Math.floor(minutes / 60);
      const mins = minutes % 60;
      const minText = `${mins} min${mins === 1 ? '' : 's'}`;
      if (!hrs) return minText;
      return `${hrs} hr${hrs === 1 ? '' : 's'}${mins ? ` ${minText}` : ''}`;
    }
  }

  customElements.define('delivery-estimate', DeliveryEstimate);
}
